import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import ReactDOM from "react-dom";
import {
  type NodeProps,
  type Node,
  Handle,
  Position,
  useReactFlow,
} from "@xyflow/react";
import {
  Trash2,
  Settings,
  X,
  AlertTriangle,
  Loader2,
  CheckCircle,
  Network,
  ClipboardCopy,
  Radio,
} from "lucide-react";
import apiClient from "../../../api/api";
import { API_ENDPOINTS } from "../../../api/api.config";
import NodeDocsButton from "../../docs/NodeDocsButton";

export const HTTPRequestNode: React.FC<NodeProps<Node<any>>> = ({
  id,
  data,
  selected,
}) => {
  const { setNodes, deleteElements } = useReactFlow();

  // ❌ Node name removed
  // const [httpName, setHttpName] = useState<string>(data?.http_name || "HTTP Request");

  const [method] = useState<string>(data?.method || "POST");
  const [params, setParams] = useState<string>(data?.params || "/execute");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPolling, setIsPolling] = useState(false);

  const pollingIntervalRef = useRef<any | null>(null);
  const pollingAttemptsRef = useRef(0);
  const hasStartedPollingRef = useRef(false);

  const baseUrl = `${
    import.meta.env.VITE_DMS_API
  }/dms-v2-srv/workflow-http-node-srv`;
  const templateId = data?.request_id || "";
  const executionStatus = data?.executionStatus || null;

  const POLLING_INTERVAL = 2000; // 2 seconds
  const MAX_POLLING_ATTEMPTS = 150; // 5 minutes max

  const isExecuting = executionStatus === "running";
  const executionSuccess = executionStatus === "success";
  const executionError = executionStatus === "error";

  const stopPropagation = useCallback(
    (e: React.MouseEvent | React.FocusEvent) => e.stopPropagation(),
    [],
  );

  const handleDeleteNode = useCallback(() => {
    deleteElements({ nodes: [{ id }] });
  }, [deleteElements, id]);

  const handleOpenModal = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => setIsModalOpen(false), []);
  const cleanParams = params?.startsWith("/") ? params.slice(1) : params;

  // Stop polling
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setIsPolling(false);
    pollingAttemptsRef.current = 0;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  // Poll execution to get outputs
  const pollExecution = useCallback(async () => {
    if (!templateId) {
      console.error("❌ HTTP Node - No template ID available for polling");
      stopPolling();
      return;
    }

    try {
      const response = await apiClient.get(
        API_ENDPOINTS.WORKFLOW.EXECUTION_BY_TEMPLATE_ID(templateId),
      );

      if (response.data.success && response.data.data) {
        const executionData = response.data.data;

        console.log(
          "📊 HTTP Node - Polling (attempt " + pollingAttemptsRef.current + ")",
        );

        // Find this node in nodes array to get data
        const thisNode = executionData.nodes?.find(
          (n: any) => n.type === "HTTP" || n.id === id,
        );

        // Update node data with inputs and outputs if available
        if (thisNode?.data) {
          const updatedData: any = {
            ...data,
            name: "HttpNode", // Preserve node name
          };

          // Update inputs if available
          if (thisNode.data.inputs) {
            Object.keys(thisNode.data.inputs).forEach((key) => {
              updatedData[key] = thisNode.data.inputs[key];
            });
            console.log("📥 HTTP Node - Inputs updated:", thisNode.data.inputs);
          }

          // Update outputs if available
          if (thisNode.data.outputs) {
            updatedData.outputs = thisNode.data.outputs;
            console.log(
              "📤 HTTP Node - Outputs updated:",
              thisNode.data.outputs,
            );
          }

          // Update node state
          setNodes((nds) =>
            nds.map((node) =>
              node.id === id
                ? {
                    ...node,
                    name: "HttpNode", // Set name at node root level
                    data: updatedData,
                  }
                : node,
            ),
          );
        }

        // Check overall processing status
        const overallStatus = executionData.overall_proccessing_status;
        console.log("📊 HTTP Node - Overall status:", overallStatus);

        // If workflow is DONE and we have outputs, stop polling
        if (overallStatus === "DONE" && thisNode?.data?.outputs) {
          console.log(
            "✅ HTTP Node - Workflow complete with outputs, stopping polling",
          );
          stopPolling();
          return true; // Polling complete
        }

        // If workflow is FAILED or ERROR, continue polling to listen for next execution
        if (overallStatus === "FAILED" || overallStatus === "ERROR") {
          console.error(
            "❌ HTTP Node - Workflow execution failed, continuing to listen for next execution...",
          );
          return false; // Continue polling
        }

        // Continue polling while workflow is in progress
        console.log(
          "⏳ HTTP Node - Workflow in progress, continuing to listen...",
        );
        return false;
      }

      return false;
    } catch (error) {
      console.error("❌ HTTP Node - Failed to poll execution:", error);
      // Don't stop polling on error, might be temporary
      return false;
    }
  }, [templateId, id, setNodes, stopPolling]);

  // Start polling automatically when templateId is available
  useEffect(() => {
    // Only start if we have templateId, no outputs yet, and haven't started polling
    if (
      templateId &&
      !data?.outputs &&
      !hasStartedPollingRef.current &&
      !isPolling
    ) {
      console.log(
        "🎧 HTTP Node - Auto-starting polling for templateId:",
        templateId,
      );
      hasStartedPollingRef.current = true;
      setIsPolling(true);
      pollingAttemptsRef.current = 0;

      // Poll immediately
      pollExecution();

      // Set up polling interval
      pollingIntervalRef.current = setInterval(async () => {
        if (pollingAttemptsRef.current >= MAX_POLLING_ATTEMPTS) {
          console.warn("⚠️ HTTP Node - Max polling attempts reached");
          stopPolling();
          return;
        }

        pollingAttemptsRef.current += 1;
        const shouldStop = await pollExecution();
        if (shouldStop) {
          stopPolling();
        }
      }, POLLING_INTERVAL);
    }

    // If outputs are already available, mark as started
    if (data?.outputs && !hasStartedPollingRef.current) {
      hasStartedPollingRef.current = true;
    }
  }, [
    templateId,
    data?.outputs,
    isPolling,
    pollExecution,
    stopPolling,
    POLLING_INTERVAL,
    MAX_POLLING_ATTEMPTS,
  ]);

  useEffect(() => {
    setNodes((nds) =>
      nds.map((node) =>
        node.id === id
          ? {
              ...node,
              name: "HttpNode", // Set name at node root level
              data: {
                ...node.data,
                name: "HttpNode", // Also set in data for consistency
                url_name: cleanParams,
                method,
                trigger_url: `${baseUrl}${params}/${templateId || ""}`,
              },
            }
          : node,
      ),
    );
  }, [id, setNodes, method, params, templateId]);

  const statusConfig = useMemo(() => {
    if (isPolling) {
      return { bg: "bg-yellow-500 animate-pulse", text: "Listening..." };
    }
    if (data?.outputs) {
      return { bg: "bg-green-500", text: "Ready" };
    }
    const ready = params && templateId;
    return ready
      ? { bg: "bg-indigo-500", text: "Configured" }
      : { bg: "bg-gray-400", text: "Not Configured" };
  }, [params, templateId, isPolling, data?.outputs]);

  const fullEndpoint = useMemo(
    () =>
      params
        ? `${baseUrl}${params}/${templateId || "<request_id>"}`
        : `${baseUrl}/...`,
    [baseUrl, params, templateId],
  );

  return (
    <div
      className={`relative group bg-indigo-50 border-2 rounded-xl p-4 min-w-[280px] max-w-[320px] shadow-sm transition-all ${
        isPolling
          ? "border-yellow-500 shadow-lg shadow-yellow-200"
          : isExecuting
            ? "border-blue-500 shadow-lg shadow-blue-200 animate-pulse"
            : executionSuccess
              ? "border-green-500 shadow-md shadow-green-200"
              : executionError
                ? "border-red-500 shadow-md shadow-red-200"
                : selected
                  ? "border-indigo-500 shadow-md"
                  : "border-indigo-300 hover:border-indigo-300"
      }`}
    >
      {/* Status Overlays */}
      {isPolling && (
        <div className="absolute top-2 right-2 z-10">
          <div className="bg-yellow-500 rounded-full p-1.5 shadow-lg animate-pulse">
            <Radio className="w-4 h-4 text-white" />
          </div>
        </div>
      )}
      {isExecuting && (
        <div className="absolute inset-0 bg-blue-500/10 backdrop-blur-[1px] rounded-xl flex items-center justify-center z-10">
          <div className="bg-white rounded-full p-3 shadow-lg">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
        </div>
      )}
      {executionSuccess && !isPolling && (
        <div className="absolute -top-3 -right-4 z-30 pointer-events-none">
          <div className="bg-green-500 rounded-full p-1.5 border-2 border-white">
            <CheckCircle className="w-4 h-4 text-white" />
          </div>
        </div>
      )}
      {executionError && (
        <div className="absolute -top-3 -right-4 z-30 pointer-events-none">
          <div className="bg-red-500 rounded-full p-1.5 border-2 border-white">
            <AlertTriangle className="w-4 h-4 text-white" />
          </div>
        </div>
      )}

      {/* Node Header */}
      <div className="flex items-start justify-between mb-3 cursor-move">
        <div className="flex items-center gap-2.5">
          <div
            className={`p-1.5 rounded-lg shadow-sm transition-all ${
              isPolling ? "bg-yellow-100" : "bg-indigo-100"
            }`}
          >
            <Network
              className={`w-4 h-4 ${
                isPolling ? "text-yellow-700" : "text-indigo-700"
              }`}
            />
          </div>
          <div>
            <h3 className="font-medium text-gray-800 text-sm">HTTP Trigger</h3>
            <div className="flex items-center gap-1.5 mt-0.5">
              <div className={`w-1.5 h-1.5 rounded-full ${statusConfig.bg}`} />
              <p className="text-xs text-gray-600">{statusConfig.text}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <NodeDocsButton nodeType="HTTP" />
          <button
            onClick={handleDeleteNode}
            className="text-red-500 hover:text-gray-700 p-1 rounded transition"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Node Body */}
      <div
        className="p-2.5 bg-white/80 border border-indigo-200 rounded-lg text-xs space-y-1.5 shadow-inner"
        onClick={stopPropagation}
      >
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
            {method}
          </span>
          <span className="text-gray-600 truncate flex-1 font-mono text-[10px]">
            {fullEndpoint}
          </span>
        </div>
      </div>

      <div className="mt-3" onClick={stopPropagation}>
        <button
          onClick={handleOpenModal}
          className="w-full py-2.5 rounded-lg bg-indigo-600 text-white text-xs font-medium flex justify-center gap-2 shadow-sm hover:bg-indigo-700 transition-colors"
        >
          <Settings className="w-3.5 h-3.5" /> Configure Trigger
        </button>
      </div>

      <Handle
        type="source"
        position={Position.Right}
        className="w-3 h-3 bg-indigo-500 border-2 border-white"
        style={{
          boxShadow:
            "0 0 0 4px rgba(99, 102, 241, 0.25), 0 6px 12px -2px rgba(0, 0, 0, 0.2)",
        }}
      />

      {/* Modal */}
      {isModalOpen &&
        ReactDOM.createPortal(
          <div
            className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center p-4"
            onClick={handleCloseModal}
          >
            <div
              className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
              onClick={stopPropagation}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-50 rounded">
                    <Network className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-gray-900">
                      Configure HTTP Trigger
                    </h2>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Define endpoint parameters and view outputs
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-hidden flex divide-x divide-gray-200">
                {/* Left Column - Configuration */}
                <div className="flex-1 overflow-y-auto bg-white p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    Endpoint Configuration
                  </h3>
                  <p className="text-sm text-gray-500 mb-6">
                    Configure the endpoint path and view the full URL.
                  </p>

                  <div className="space-y-6">
                    {/* Full URL Display */}
                    <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                        Full Endpoint URL
                      </label>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 text-xs font-mono text-indigo-700 bg-white px-3 py-2.5 rounded border border-gray-200 break-all shadow-sm">
                          {fullEndpoint}
                        </code>
                        <button
                          onClick={() =>
                            navigator.clipboard.writeText(fullEndpoint)
                          }
                          className="p-2.5 bg-white border border-gray-200 text-gray-500 hover:text-indigo-600 hover:border-indigo-200 rounded-lg transition-all shadow-sm"
                          title="Copy URL"
                        >
                          <ClipboardCopy className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Endpoint Path Input */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Endpoint Path <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={params}
                        onChange={(e) => setParams(e.target.value)}
                        placeholder="/execute"
                        required
                        autoComplete="off"
                        className="w-full px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-700 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition font-mono"
                      />
                      <p className="text-xs text-gray-500 mt-1.5">
                        Base URL: <span className="font-mono">{baseUrl}</span>
                      </p>
                    </div>
                  </div>
                </div>

                {/* Right Column - Outputs */}
                <div className="w-[400px] bg-gray-50 overflow-y-auto p-4 flex flex-col">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                      <div
                        className={`w-2 h-2 rounded-full ${
                          isPolling
                            ? "bg-yellow-500 animate-pulse"
                            : data?.outputs
                              ? "bg-green-500"
                              : "bg-gray-400"
                        }`}
                      ></div>
                      Execution Outputs
                    </h3>
                    {isPolling && (
                      <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-yellow-100 text-yellow-700 text-[10px] font-medium border border-yellow-200">
                        <Radio className="w-3 h-3 animate-pulse" />
                        Listening...
                      </span>
                    )}
                  </div>

                  <div className="flex-1 bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden flex flex-col">
                    {data?.outputs ? (
                      <div className="p-3 overflow-auto custom-scrollbar h-full">
                        <pre className="text-xs font-mono text-gray-700 whitespace-pre-wrap break-all">
                          {JSON.stringify(data.outputs, null, 2)}
                        </pre>
                      </div>
                    ) : (
                      <div className="flex-1 flex flex-col items-center justify-center p-6 text-center text-gray-400">
                        {isPolling ? (
                          <>
                            <Loader2 className="w-8 h-8 mb-3 animate-spin text-indigo-400" />
                            <p className="text-sm font-medium text-gray-600">
                              Waiting for data...
                            </p>
                            <p className="text-xs mt-1">
                              Listening for incoming trigger
                            </p>
                          </>
                        ) : (
                          <>
                            <Network className="w-8 h-8 mb-3 opacity-20" />
                            <p className="text-sm">No outputs available</p>
                            <p className="text-xs mt-1">
                              Save workflow to generate endpoint
                            </p>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
                <button
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors shadow-sm"
                >
                  Save Configuration
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default React.memo(HTTPRequestNode);
